export type InstantAnswerDisclaimers = Record<
  string,
  {
    en: string;
    es: string;
  }
>;

type LocaleResponse = {
  phrases: string[];
  response: string;
  badgeLabel?: string;
};

export type GuestIntentRule = {
  id: string;
  category: 'faq' | 'policy' | 'allergen';
  priority: number;
  tags: string[];
  requiresPremiumFallback?: boolean;
  locales: Record<'en' | 'es', LocaleResponse>;
};

export type GuestIntentLibrary = {
  version: string;
  disclaimers: InstantAnswerDisclaimers;
  rules: GuestIntentRule[];
};

export const ASSISTANT_INSTANT_ANSWER_VERSION = '2025.02.17';

export const assistantInstantAnswers: GuestIntentLibrary = {
  version: ASSISTANT_INSTANT_ANSWER_VERSION,
  disclaimers: {
    allergen: {
      en: 'Allergen notice: Our kitchen handles nuts, dairy, gluten, soy, and shellfish. Team members can double-check every item and we sanitize tools, but cross-contact is still possible.',
      es: 'Alerta de alérgenos: Nuestra cocina trabaja con frutos secos, lácteos, gluten, soya y mariscos. El equipo puede confirmar cada platillo y desinfectamos las herramientas, pero siempre existe riesgo de contacto cruzado.',
    },
    alcohol: {
      en: 'Alcohol service: Please have a valid ID ready. Staff may decline or limit service to keep everyone safe.',
      es: 'Servicio de alcohol: Ten tu identificación vigente a la mano. El equipo puede rechazar o limitar el servicio para mantener a todos seguros.',
    },
  },
  rules: [
    {
      id: 'wifi',
      category: 'faq',
      priority: 90,
      tags: ['wifi', 'internet', 'network'],
      locales: {
        en: {
          phrases: ['wifi', 'wi-fi', 'internet', 'network', 'password'],
          response:
            'Guest Wi‑Fi info lives on the QR card and at the host stand. Open settings, choose the network listed on your table (usually “Guest” or the venue name), enter the short passcode on the card, and you are online. If it is missing, wave down a teammate and we will read it back for you.',
          badgeLabel: 'Instant answer',
        },
        es: {
          phrases: ['wifi', 'wi fi', 'clave', 'internet', 'red'],
          response:
            'La información del Wi‑Fi para huéspedes está impresa en la tarjeta QR y en la mesa de anfitriones. Abre configuraciones, selecciona la red indicada en tu mesa (generalmente “Guest” o el nombre del lugar), ingresa la clave corta del impreso y listo. Si no ves la tarjeta, pídesela a cualquier miembro del equipo.',
          badgeLabel: 'Respuesta al instante',
        },
      },
    },
    {
      id: 'hours',
      category: 'faq',
      priority: 70,
      tags: ['hours', 'time', 'close', 'open'],
      locales: {
        en: {
          phrases: ['hours', 'time', 'closing', 'open'],
          response:
            'Today’s dining hours are posted near the host stand and in the banner of this menu. We give last call about 30 minutes before closing so the kitchen can finish strong-flag a teammate if you need the exact time for today.',
          badgeLabel: 'Instant answer',
        },
        es: {
          phrases: ['horario', 'hora', 'cierran', 'abren'],
          response:
            'El horario de hoy está publicado junto al host y en el encabezado de este menú. Hacemos el último pedido unos 30 minutos antes del cierre para que la cocina termine tranquila; avisa a un miembro del equipo si necesitas la hora exacta para hoy.',
          badgeLabel: 'Respuesta al instante',
        },
      },
    },
    {
      id: 'menu-basics',
      category: 'faq',
      priority: 60,
      tags: ['popular', 'recommend', 'special', 'menu'],
      locales: {
        en: {
          phrases: ['popular', 'recommend', 'favorite', 'special'],
          response:
            'Our fan favorites are marked with the ✨ or “Guest Pick” badge in this menu. Tap any item to see ingredients, prep time, and modifier notes. If you need a live recommendation (kid-friendly, vegetarian, fast-fire) just tell us and we will narrow it down.',
          badgeLabel: 'Instant answer',
        },
        es: {
          phrases: ['popular', 'recomienda', 'favorito', 'especial'],
          response:
            'Nuestros platillos más pedidos tienen el ícono ✨ o la insignia “Favorito de huéspedes” en este menú. Toca cada platillo para ver ingredientes, tiempo de preparación y modificaciones. Si quieres una recomendación en vivo (para niños, vegetariana, rápida), avísanos y con gusto la afinamos.',
          badgeLabel: 'Respuesta al instante',
        },
      },
    },
    {
      id: 'allergens',
      category: 'allergen',
      priority: 95,
      tags: ['allergy', 'allergen', 'gluten', 'peanut', 'dairy', 'shellfish'],
      requiresPremiumFallback: true,
      locales: {
        en: {
          phrases: ['allergy', 'allergen', 'gluten', 'nut', 'dairy', 'shellfish'],
          response:
            'We log every allergy so the kitchen can separate tools and call out the ticket. Add your allergen note in the cart or let a teammate know and we will double-check the line. {{allergen}}',
          badgeLabel: 'Safety notice',
        },
        es: {
          phrases: ['alergia', 'alérgeno', 'gluten', 'nuez', 'lácteo', 'marisco'],
          response:
            'Registramos cada alergia para separar utensilios y avisar al equipo de cocina. Agrega la nota en el carrito o coméntala al personal y la verificamos juntos en la línea. {{allergen}}',
          badgeLabel: 'Aviso de seguridad',
        },
      },
    },
    {
      id: 'house-policy',
      category: 'policy',
      priority: 65,
      tags: ['policy', 'outside food', 'pet', 'dress', 'reservation', 'corkage', 'alcohol'],
      requiresPremiumFallback: true,
      locales: {
        en: {
          phrases: ['policy', 'outside', 'pet', 'dress', 'corkage', 'id', 'alcohol'],
          response:
            'House policies are posted near the entry: outside food stays wrapped unless cleared by the manager, pets must remain on leash on the patio, and we can check IDs at any time. For corkage or celebration items, a teammate will outline the quick fee before you open it. {{alcohol}}',
          badgeLabel: 'Policy reminder',
        },
        es: {
          phrases: ['política', 'externa', 'mascota', 'vestimenta', 'descorche', 'identificación', 'alcohol'],
          response:
            'Las políticas están en la entrada: la comida externa debe permanecer cerrada salvo autorización del gerente, las mascotas deben estar con correa en el patio y podemos pedir identificación en cualquier momento. Para descorche o pasteles externos, el equipo explicará la cuota antes de servir. {{alcohol}}',
          badgeLabel: 'Aviso de política',
        },
      },
    },
  ],
};
